import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { useApplicationProductsQuery } from './useApplicationProductsQuery';
import { useApplicationProductsSearch } from './useApplicationProductsSearch';
import { ApplicationProduct } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is NULLABLE on ApplicationProduct (types.ts). The checklist
// prefers an immutable, non-null key, and there is no better one here: `id` is
// opaque, and `position` is operator-assigned and therefore mutable. `created`
// is still the right choice — it is immutable (unlike `updated`, which would
// reorder the list under someone reading it), and listApplicationProducts
// already returns created-descending, so the first paint AGREES with the server
// rather than reshuffling it. Rows missing it sort last, which is correct.
const DEFAULT_SORT_STATUS: DataTableSortStatus<ApplicationProduct> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const useApplicationProducts = (applicationId?: string) => {

    const filters: Record<string, string> = {};
    if (applicationId) {
        filters.applicationId = applicationId;
    }

    const query = useApplicationProductsQuery(filters);
    const { keyword } = useApplicationProductsSearch();

    // Type guard, not a bare flatMap: a page can be undefined while a fetch is
    // in flight, and `page.items` would throw on it.
    const items: ApplicationProduct[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen, so the footer states a LOADED count
    // rather than a total, and `unfilteredCount` travels with the match count
    // so an operator can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.productId?.toLowerCase().includes(needle) ||
        item.product?.name?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<ApplicationProduct>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        visibleCount: visibleItems.length,
        unfilteredCount: items.length,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
